import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Card, Link } from '@americanexpress/dls-react';
import { IconCardSwipe, IconP2p } from '@americanexpress/dls-icons';
import { v4 as uuidv4 } from 'uuid';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { cn } from '@/helpers/utils';
import { useModal } from '@/context/modalContext';
import styles from './LoungeDetails.module.scss';
import LoungeAccessProductDescription from '../common/LoungeAccessProductDescription';
import PolicyButton from '../common/PolicyButton';
import { modalTypes } from '../common/modals/constants';
import WaitListButton from '../waitList/WaitListButton';
import touchTargetStyles from '../common/TargetTouchSize.module.scss';
import type { LoungeDetailsSidebarProps } from './LoungeDetailsSidebar/types';

type ToggleModalParams = {
  detail?: string;
  element: string;
  event: React.MouseEvent<HTMLElement>;
  modalName: string;
};

const LoungeDetailsSidebar = ({
  className,
  sidebarData,
}: LoungeDetailsSidebarProps) => {
  const {
    accessPolicyHeading,
    baseOneCmsImgUrl,
    cardImageSrc,
    cardSwitchLinkText,
    guestPolicyHeading,
    hasAccessPolicy,
    hasGuestPolicy,
    hasWalletProduct,
    isWaitListAvailableAtLounge,
    locale,
    loggedInCardPill,
    product,
    termsLinkText,
  } = sidebarData;
  const { customTrack } = useAnalyticsContext();
  const { setModalName } = useModal();
  const accessButtonId = uuidv4();
  const guestButtonId = uuidv4();
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    setIsImageLoading(true);
    const image = new Image();
    image.src = cardImageSrc;

    if (image.complete) {
      setIsImageLoading(false);
    }
  }, [cardImageSrc]);

  const handleToggleModal = ({
    modalName,
    ...trackingProps
  }: ToggleModalParams) => {
    setModalName(modalName);
    customTrack(trackingProps);
  };

  return (
    <div
      className={cn(
        className,
        'margin-3-b',
        styles.loungeOverviewSidebarContainer
      )}
    >
      <div className={cn(styles.loungeOverviewSidebar, 'tw-overflow-hidden')}>
        <Card
          className={cn('tw-overflow-hidden', styles.accessPanelCardContainer)}
          aria-label="Card Callout Notification"
          style={{
            minWidth: 'unset',
            backgroundImage: `url(${cardImageSrc})`,
            backgroundSize: 'cover',
          }}
        >
          <div
            className="tw-backdrop-blur-1dot5xl"
            style={{ marginBlockEnd: '0' }}
          />
          <div className="tw-flex tw-justify-center tw-px-2">
            <img
              className={cn(
                isImageLoading && 'hidden',
                'tw-rounded-2xl tw-shadow-high sm_tw-mt-2'
              )}
              src={cardImageSrc}
              alt="Card Callout Notification"
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </div>
        </Card>
        <div className="tw-bg-white">
          <div className="tw-p-6 tw-pb-1 tw-font-sans tw-text-xl">
            {hasWalletProduct && (
              <div className="tw-mb-3 tw-w-fit tw-rounded tw-bg-gradient-to-r tw-from-blue-deep-700 tw-to-blue-bright-shade-600-s8 tw-px-2 tw-py-1 tw-text-sm tw-font-bold tw-uppercase tw-text-white">
                {loggedInCardPill}
              </div>
            )}
            <LoungeAccessProductDescription locale={locale} product={product} />
          </div>
          <div className="tw-pb-4 tw-pl-6">
            <button
              id="select-card-button"
              type="button"
              className={clsx(
                'tw-text-base tw-text-blue-bright-shade-600-s8 hover_tw-underline',
                touchTargetStyles.touchTarget
              )}
              onClick={(event) =>
                handleToggleModal({
                  event,
                  element: 'change card button',
                  detail: 'card call out notification',
                  modalName: modalTypes.SELECT_CARD_MODAL,
                })
              }
            >
              {cardSwitchLinkText}
            </button>
          </div>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <div className="tw-px-4">
            {hasAccessPolicy && (
              <>
                <PolicyButton
                  id={`access-policy-${accessButtonId}`}
                  heading={accessPolicyHeading}
                  modalType={modalTypes.ACCESS_POLICY_MODAL}
                  icon={<IconCardSwipe size="md" />}
                  variant="legacy"
                />
                {hasGuestPolicy && (
                  <hr
                    className="tw-mx-2 tw-border-gray-300"
                    aria-hidden="true"
                    style={{ width: 'unset' }}
                  />
                )}
              </>
            )}
            {hasGuestPolicy && (
              <PolicyButton
                id={`guest-policy-${guestButtonId}`}
                heading={guestPolicyHeading}
                modalType={modalTypes.GUEST_POLICY_MODAL}
                icon={<IconP2p size="md" />}
                variant="legacy"
              />
            )}
          </div>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <div className="margin-3-lr pad-2-t pad-2-b">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- link opens the additional information modal */}
            <Link
              className="tw-font-medium tw-text-link"
              onClickCapture={(event) => {
                event.stopPropagation();
                handleToggleModal({
                  event,
                  element: 'terms and conditions',
                  modalName: modalTypes.TERMS_MODAL,
                });
              }}
            >
              {termsLinkText}
            </Link>
          </div>
        </div>
      </div>
      {isWaitListAvailableAtLounge && (
        <div
          className="tw-border-blue-deep-tint-t12 tw-rounded-2xl tw-bg-blue-deep-700 tw-bg-contain tw-bg-no-repeat"
          style={{
            backgroundImage: `url(${baseOneCmsImgUrl}amex-world-service-logo-accent.png)`,
          }}
        >
          <WaitListButton />
        </div>
      )}
    </div>
  );
};

export default LoungeDetailsSidebar;
